import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, MessageSquare, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultContent = {
  announcement: {
    enabled: true,
    message: "",
  },
  login_popup: {
    enabled: true,
    title: "",
    subtitle: "",
    message: "",
    email: "",
    discord_url: "",
  },
};

export const SiteContentManagement = ({ token }) => {
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/admin/site-content`, { headers })
      .then((response) => setContent({
        announcement: { ...defaultContent.announcement, ...(response.data.announcement || {}) },
        login_popup: { ...defaultContent.login_popup, ...(response.data.login_popup || {}) },
      }))
      .catch(() => toast.error("Failed to load site messages"))
      .finally(() => setLoading(false));
  }, [token]);

  const setSectionField = (section, field, value) => {
    setContent((previous) => ({
      ...previous,
      [section]: {
        ...previous[section],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.put(`${API}/admin/site-content`, content, { headers });
      setContent({
        announcement: { ...defaultContent.announcement, ...(response.data.announcement || {}) },
        login_popup: { ...defaultContent.login_popup, ...(response.data.login_popup || {}) },
      });
      toast.success("Site messages updated");
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((item) => item.msg).filter(Boolean).join(", ")
        : detail;
      toast.error(message || "Failed to save site messages");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="rounded-2xl border border-gray-100 bg-white p-6 text-sm text-gray-500">Loading site messages...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Site Messages</h2>
        <p className="mt-1 text-sm text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Edit the sitewide announcement banner and the popup shown to logged-in users.
        </p>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#42D78A]/10">
              <Bell className="h-5 w-5 text-[#1a1a1a]" />
            </div>
            <div>
              <h3 className="font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Announcement Banner</h3>
              <p className="text-xs text-gray-500">Appears below the main navigation.</p>
            </div>
          </div>
          <Switch
            checked={content.announcement.enabled}
            onCheckedChange={(checked) => setSectionField("announcement", "enabled", checked)}
            data-testid="announcement-enabled-toggle"
          />
        </div>
        <Label className="text-sm font-medium text-gray-700">Message</Label>
        <Textarea
          value={content.announcement.message}
          onChange={(event) => setSectionField("announcement", "message", event.target.value)}
          className="mt-2 min-h-[100px] resize-y"
          data-testid="announcement-message-input"
        />
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#42D78A]/10">
              <MessageSquare className="h-5 w-5 text-[#1a1a1a]" />
            </div>
            <div>
              <h3 className="font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Logged-In Popup</h3>
              <p className="text-xs text-gray-500">Shown once per browser session after a user logs in.</p>
            </div>
          </div>
          <Switch
            checked={content.login_popup.enabled}
            onCheckedChange={(checked) => setSectionField("login_popup", "enabled", checked)}
            data-testid="login-popup-enabled-toggle"
          />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label className="text-sm font-medium text-gray-700">Title</Label>
            <Input
              value={content.login_popup.title}
              onChange={(event) => setSectionField("login_popup", "title", event.target.value)}
              className="mt-2"
              data-testid="login-popup-title-input"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">Subtitle</Label>
            <Input
              value={content.login_popup.subtitle}
              onChange={(event) => setSectionField("login_popup", "subtitle", event.target.value)}
              className="mt-2"
              data-testid="login-popup-subtitle-input"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-sm font-medium text-gray-700">Message</Label>
            <Textarea
              value={content.login_popup.message}
              onChange={(event) => setSectionField("login_popup", "message", event.target.value)}
              className="mt-2 min-h-[140px] resize-y"
              data-testid="login-popup-message-input"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">Contact Email</Label>
            <Input
              value={content.login_popup.email}
              onChange={(event) => setSectionField("login_popup", "email", event.target.value)}
              className="mt-2"
              data-testid="login-popup-email-input"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">Discord URL</Label>
            <Input
              value={content.login_popup.discord_url}
              onChange={(event) => setSectionField("login_popup", "discord_url", event.target.value)}
              className="mt-2"
              data-testid="login-popup-discord-input"
            />
          </div>
        </div>
      </div>

      <Button
        onClick={handleSave}
        disabled={saving}
        className="bg-[#42D78A] text-[#1a1a1a] hover:bg-[#3bc27c] rounded-full px-6 font-bold"
        data-testid="save-site-content-btn"
      >
        <Save className="mr-2 h-4 w-4" />
        {saving ? "Saving..." : "Save Messages"}
      </Button>
    </div>
  );
};
